'use client';

import { createContext, useCallback, useContext, useState } from 'react';

type Locale = 'fa' | 'ar' | 'en' | 'pa';
type Direction = 'rtl' | 'ltr';
type Dictionary = Record<string, any>;
type TranslationParams = Record<string, string | number>;

type DictionaryContextType = {
  dictionary: Dictionary | null;
  locale: Locale;
  dir: Direction;
  t: (key: string, params?: TranslationParams) => string;
}

const DictionaryContext = createContext<DictionaryContextType | null>(null);

type DictionaryProviderProps = {
  children: React.ReactNode;
  initialDictionary: Dictionary;
  initialLocale: Locale;
  loadDictionary?: (locale: Locale) => Promise<Dictionary>;
}

export const DictionaryProvider = ({
  children,
  initialDictionary,
  initialLocale,
}: DictionaryProviderProps) => {

  const [dictionary, setDictionary] = useState<Dictionary | null>(initialDictionary);
  const [locale, setLocale] = useState<Locale>(initialLocale);

  const translate = useCallback((key: string, params?: TranslationParams): string => {
    const value = key.split('.').reduce((obj, k) =>
      obj && typeof obj === 'object' ? obj[k] : undefined,
      dictionary as any);

    if (value === undefined) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Missing translation: "${key}" for locale "${locale}"`);
      }
      return key;
    }

    return Object.keys(params || {}).reduce(
      (result, paramKey) => result.replace(new RegExp(`{${paramKey}}`, 'g'), String(params![paramKey])),
      String(value)
    );
  }, [dictionary, locale]);

  const contextValue = {
    dictionary,
    locale,
    dir: ['fa', 'ar', 'pa'].includes(locale) ? 'rtl' : 'ltr' as Direction,
    t: translate
  }

  return (
    <DictionaryContext.Provider value={contextValue}>
      {children}
    </DictionaryContext.Provider>
  );
};

export function useDictionary() {
  const context = useContext(DictionaryContext);
  if (!context) {
    throw new Error('useDictionary must be used within a DictionaryProvider');
  }
  return context;
}